import { ReactNode, useCallback, useEffect, useState } from "react";

import Button from "components/Button";
import { ProjectBreadcrumbPage } from "components/ProjectBreadcrumbPage";
import SemiCircleChart from "components/SemiCircleChart";
import { projectConfigToShow }  from "components/hosted-scraping/showProjectConfig";

import scraperApi from "api";

import { useHostedScrapingProjects } from "providers/HostedScrapingProvider";
import { useProjectCostEstimate } from "hooks/useProjectCostEstimate";
import { useLegacyRate } from "hooks/useLegacyRate";
import { isQuotableProject, legacyRateOptionsFromApiParams } from "utils/legacyRate";
import { LegacyRateCallout } from "components/LegacyRateCallout";
import { ProjectConfigWithStats } from "providers/HostedScrapingProvider/types";
import { Navigate, useParams } from "react-router-dom";
import { ProjectStatus } from "components/hosted-scraping/ProjectStatus";
import { JobList } from "components/hosted-scraping/JobList";

import { ReactComponent as ProjectDetailsEqualizer } from "assets/icons/project-details-config-equalizer.svg";
import { ReactComponent as ProjectDetailsLink } from "assets/icons/project-details-config-link.svg";
import { ReactComponent as ProjectDetailsRoundThingy } from "assets/icons/project-details-config-roundes-thingy.svg";

import { ExclamationCircleIcon, PlayIcon } from  "@heroicons/react/outline"
import { Tab } from "@headlessui/react";

import { formatDate } from "components/hosted-scraping/ProjectFormatDate";
import { formatCreditsSpent } from "components/hosted-scraping/creditsSpent";

import { ProjectDetailsHead } from "components/hosted-scraping/ProjectDetailsHead";
import { ProjectNotFound } from "components/hosted-scraping/ProjectNotFound";
import { FullWidthLoadingSpinner } from "components/FullWidthLoadingSpinner";
import { IconName } from "components/Icons";
import Toaster from "components/Toaster";
import { tooltips } from "components/hosted-scraping/edit-project-components/tooltips";
import { PureTooltip } from "components/Tooltip";

import BorderLayout from "layouts/BorderLayout";

import { followRedirectToDisableFollowRedirect } from "providers/HostedScrapingProvider/negateFollowRedirects";
import { useRecentlyViewedPage } from "providers/RecentlyViewed/RecentlyViewedProvider";

import { cx, toNumber } from "utils";
import { compareDates } from "utils/dateUtils";
import {
  iconType,
  isAmazonProject,
  isAsyncUrlsLlmProject,
  isAsyncUrlsProject,
  isEbayProject,
  isGoogleProject,
  isHomeDepotSearchProject,
  isHomeDepotProductProject,
  isHomeDepotReviewsProject,
  isRedfinProject,
  isWalmartProject,
  getSdeTrackingInfo
} from "sdecontent";
import { trackDataPipelineRunJobNow } from "utils/Tracking";
import { BillingPath } from "v2/pages/billing/BillingPath";
import { useUser } from "routes/dataroutes/UserData";


const formatSupposedToRunAt = (scheduledAt: Date | null | string | undefined): string => {
  const now = new Date();
  const schAt = new Date(scheduledAt || Date.now());
  if (!Boolean(scheduledAt) || schAt > now) {
    return formatDate(scheduledAt);
  } else {
    return 'Any time now'
  }
}

const SuccessAndFailureRateChart = ({project}: {project: ProjectConfigWithStats}) => {
  //const finishedJobs = project.doneJobsCount + project.errorJobsCount;
  const finishedTasks = project.successfulTasks + project.failedTasks;
  const doneRate = finishedTasks >= 1 ? ((project.successfulTasks / finishedTasks) * 100.0).toFixed(0) : '-';
  const errorRate = finishedTasks >= 1 ?((project.failedTasks / finishedTasks) * 100.0).toFixed(0) : '-';
  const doneLabel = `Success (${doneRate}%)`;
  const errorLabel = `Failure (${errorRate}%)`;

  return (<div className="self-center w-full flex flex-row">
    <div>
      <SemiCircleChart success={project.successfulTasks} failure={project.failedTasks} circumference={360} legend={[doneLabel, errorLabel]} legendPosition="right"/>
    </div>
  </div>);
};

const creditsPerFinishedJob = (project: ProjectConfigWithStats): number | undefined => {
  const { credits, doneJobsCount, errorJobsCount } = project;
  const finishedJobs = doneJobsCount + errorJobsCount;

  if (finishedJobs === 0 || finishedJobs === undefined || finishedJobs === null) {
    return undefined;
  }

  return credits / finishedJobs;
}

const generalStatusLineStyle = "flex flex-row items-center justify-between w-96 text-sm font-normal";
const generalStatsLeftSideStyle = "w-40 text-gray dark:text-neutral-600 justify-self-start";
const generalStatsRightSideStyle = "whitespace-nowrap w-1/2 font-medium";
const GeneralStats = ({project}: {project: ProjectConfigWithStats}) => (
  <div className="w-full h-full grid font-normal text-base projectStatsBreak:grid-rows-6 projectStatsBreak:grid-cols-1 grid-rows-6 grid-cols-1 grid-flow-col justify-items-start">
    <div className={generalStatusLineStyle}>
      <div className="flex flex-row items-center">
        {/* <div className="mr-2 ml-2"><ProjectStatusIcon/></div> */}
        <div className={generalStatsLeftSideStyle}>
          {/* <Tooltip content={tooltips.projectsStatus}>Status</Tooltip> */}
          Status
        </div>
      </div>
      <div className={generalStatsRightSideStyle}>
        <ProjectStatus project={project}/>
      </div>
    </div>
    <div className={generalStatusLineStyle}>
      <div className="flex flex-row items-center">
        {/* <div className="mr-2 ml-2"><CalendarCreatedIcon/></div> */}
        <div className={generalStatsLeftSideStyle}>
          {/* <Tooltip content={tooltips.projectsCreated}>Created</Tooltip> */}
          Created
        </div>
      </div>
      <div className={generalStatsRightSideStyle}>
        {formatDate(project.createdAt)}
      </div>
    </div>

    <div className={generalStatusLineStyle}>
      <div className="flex flex-row items-center">
        {/* <div className="mr-2 ml-2"><CalendarLastRunIcon/></div> */}
      <div className={generalStatsLeftSideStyle}>
        {/* <Tooltip content={tooltips.projectsScheduled}>Scheduled</Tooltip> */}
        Scheduled
        </div>
      </div>
      <div className={generalStatsRightSideStyle}>
        {formatSupposedToRunAt(project.supposedToRunAt)}
      </div>
    </div>

    <div className={generalStatusLineStyle}>
      <div className="flex flex-row items-center">
        {/* <div className="mr-2 ml-2"><CostPerRequestIcon/></div> */}
        <div className={generalStatsLeftSideStyle}>
          {/* <Tooltip content={tooltips.projectsCostPerJob}>Cost per job</Tooltip> */}
          Cost per job
        </div>
      </div>
      <div className={generalStatsRightSideStyle}>{formatCreditsSpent(creditsPerFinishedJob(project))}</div>
    </div>

    <div className={generalStatusLineStyle}>
      <div className="flex flex-row items-center">
        {/* <div className="mr-2 ml-2"><CreditsSpentIcon/></div> */}
        <div className={generalStatsLeftSideStyle}>
          {/* <Tooltip content={tooltips.projectsCreditsSpent}>Credits spent</Tooltip> */}
          Credits spent
        </div>
      </div>
      <div className={generalStatsRightSideStyle}>{formatCreditsSpent(project.credits)}</div>
    </div>

    <div className={generalStatusLineStyle}>
      <div className="flex flex-row items-center max-w-[11rem]">
        {/* <div className="mr-2 ml-2"><CreditsSpentIcon/></div> */}
        {/* Note the <br/> on the next line. It's a controlled break, the layout would break the text anyway but the width of the element would be the maximum so the (?) on right side would look ugly */}
        <div className={generalStatsLeftSideStyle}>
          {/* <Tooltip content={tooltips.projectsCreditsSpentInBillingPeriod}>Credits spent in<br/>billing period</Tooltip> */}
          Credits spent in<br/>billing period
        </div>
      </div>
      <div className={generalStatsRightSideStyle}>{formatCreditsSpent(project.creditsInBillingPeriod)}</div>
    </div>
  </div>
);

const Block = ({children}: { children: ReactNode }) => {
  return (
      <div className="w-full border border-slate-200 p-5 bg-white overflow-auto">
        {children}
      </div>
  );
};

const TabButton = ({ children }: { children: ReactNode }) => {
  return (
    <Tab>
      {({ selected }) => (
        <div className={cx("px-2 pb-2 border-b-2", selected ? "border-brandPrimary dark:border-primary-600 text-brandDarkest dark:text-primary-800" : "border-transparent")}>
          {children}
        </div>
      )}
    </Tab>
  );
};

const BasicInfoAndStats = ({project}: {project: ProjectConfigWithStats}) => {
  // Legacy-rate message (ENG-1112): this page has no summary sidebar, so the chip sits under the stats, by "Cost per job".
  const legacyStandardCredits = useLegacyRate(
    isQuotableProject(project.config.type) && project.input.type === 'list_literal' ? project.input.list : undefined,
    legacyRateOptionsFromApiParams(project.config.apiParams),
    { debounceMs: 0 }
  );

  return (
    <div className="flex flex-row flex-wrap w-full justify-start gap-8 py-4">
      <div className="self-center flex-grow">
        <SuccessAndFailureRateChart project={project}/>
      </div>
      <div className="self-center flex-grow">
        <GeneralStats project={project}/>
        <LegacyRateCallout standardCredits={legacyStandardCredits} variant="inline" className="mt-3" />
        {/* <SchedulingError project={project}/> */}
      </div>
    </div>
  );
};

const ConfigurationDetailLine = ({Icon, title, children}: {Icon: any, title: string, children: ReactNode}) => (
  <div className="flex flex-row gap-1 items-start">
    <div className= "w-1/2 flex flex-row items-center">
      <div className='mr-2'><Icon/></div>
      <div className="text-sm text-gray-600 dark:text-neutral-600 pr-2">{title}</div>
    </div>
    <div className="w-full text-sm">{children}</div>
  </div>
)

const icons = {
  'round-thingy': ProjectDetailsRoundThingy,
  'link': ProjectDetailsLink,
  'equalizer': ProjectDetailsEqualizer
};

const UrlProjectAsyncApiParams = ({project}: {project: ProjectConfigWithStats}) => {
  return (<>
    {project.config?.apiParams?.premium && <ConfigurationDetailLine Icon={ProjectDetailsEqualizer} title="Premium residential and mobile IPs">Enabled</ConfigurationDetailLine>}
    {project.config?.apiParams?.ultraPremium && <ConfigurationDetailLine Icon={ProjectDetailsEqualizer} title="Advanced bypass mechanism">Enabled</ConfigurationDetailLine>}
    {followRedirectToDisableFollowRedirect(project.config?.apiParams?.followRedirect as boolean) && <ConfigurationDetailLine Icon={ProjectDetailsEqualizer} title="Follow redirects">Disabled</ConfigurationDetailLine>}
    {/* <ConfigurationDetailLine Icon={ProjectDetailsEqualizer} title="Keep headers">
      {project.config?.apiParams?.keepHeaders ? 'Enabled' : 'Disabled'}
    </ConfigurationDetailLine> */}
    {project.config?.apiParams?.render && <ConfigurationDetailLine Icon={ProjectDetailsEqualizer} title="Javascript rendering">Enabled</ConfigurationDetailLine>}
    {project.config?.apiParams?.waitForSelector && <ConfigurationDetailLine Icon={ProjectDetailsEqualizer} title="Wait for selector">{project.config?.apiParams?.waitForSelector as string}</ConfigurationDetailLine>}
    {project.config?.apiParams?.retry404 && <ConfigurationDetailLine Icon={ProjectDetailsEqualizer} title="Retry 404">Enabled</ConfigurationDetailLine>}
    {project.config?.apiParams?.autoparse && <ConfigurationDetailLine Icon={ProjectDetailsEqualizer} title="Auto parse">Enabled</ConfigurationDetailLine>}
    {project.config?.apiParams?.countryCode && <ConfigurationDetailLine Icon={ProjectDetailsEqualizer} title="Country Code">{project.config?.apiParams?.countryCode as string}</ConfigurationDetailLine>}
    {project.config?.apiParams?.deviceType && <ConfigurationDetailLine Icon={ProjectDetailsEqualizer} title="Device Type">{project.config?.apiParams?.deviceType as string}</ConfigurationDetailLine>}
    {project.config?.apiParams?.outputFormat && <ConfigurationDetailLine Icon={ProjectDetailsEqualizer} title="Output format">{project.config?.apiParams?.outputFormat as string}</ConfigurationDetailLine>}
  </>);
}

const AmazonProjectAsyncApiParams = ({project}: {project: ProjectConfigWithStats}) => {
  const params = (project.config || {});
  return (<>
    {params.apiParams?.countryCode && <ConfigurationDetailLine Icon={ProjectDetailsEqualizer} title="Country Code">{params.apiParams?.countryCode as string}</ConfigurationDetailLine>}
    {params.apiParams?.tld && <ConfigurationDetailLine Icon={ProjectDetailsEqualizer} title="Domain">{params.apiParams?.tld as string}</ConfigurationDetailLine>}
  </>);
}

const GoogleProjectAsyncApiParams = ({project}: {project: ProjectConfigWithStats}) => {
  const params = (project.config || {});
  return (<>
    {params.apiParams?.countryCode && <ConfigurationDetailLine Icon={ProjectDetailsEqualizer} title="Country Code">{params.apiParams?.countryCode as string}</ConfigurationDetailLine>}
    {params.apiParams?.tld && <ConfigurationDetailLine Icon={ProjectDetailsEqualizer} title="Domain">{params.apiParams?.tld as string}</ConfigurationDetailLine>}
    {params.apiParams?.uule && <ConfigurationDetailLine Icon={ProjectDetailsEqualizer} title="UULE">{params.apiParams?.uule as string}</ConfigurationDetailLine>}
    {/* num= is no longer offered when configuring a project, but legacy projects keep showing what they were scraped with. */}
    {params.apiParams?.num && <ConfigurationDetailLine Icon={ProjectDetailsEqualizer} title="Number of results">{String(params.apiParams?.num)} <span className="text-gray-600 dark:text-neutral-600">(no longer supported by Google)</span></ConfigurationDetailLine>}
    {params.apiParams?.additionalParams && <ConfigurationDetailLine Icon={ProjectDetailsEqualizer} title="Additional parameters">{params.apiParams?.additionalParams as string}</ConfigurationDetailLine>}
  </>);
}

const WalmartProjectAsyncApiParams = ({project}: {project: ProjectConfigWithStats}) => {
  const params = (project.config || {});
  return (<>
    {params.apiParams?.tld && <ConfigurationDetailLine Icon={ProjectDetailsEqualizer} title="Domain">{params.apiParams?.tld as string}</ConfigurationDetailLine>}
    {params.apiParams?.page && <ConfigurationDetailLine Icon={ProjectDetailsEqualizer} title="Page">{String(params.apiParams?.page)}</ConfigurationDetailLine>}
    {params.apiParams?.sort && <ConfigurationDetailLine Icon={ProjectDetailsEqualizer} title="Sort by">{String(params.apiParams?.sort)}</ConfigurationDetailLine>}
  </>);
}

const EbayProjectAsyncApiParams = ({project}: {project: ProjectConfigWithStats}) => {
  const params = (project.config || {});
  return (<>
    {params.apiParams?.tld && <ConfigurationDetailLine Icon={ProjectDetailsEqualizer} title="Domain">{params.apiParams?.tld as string}</ConfigurationDetailLine>}
    {params.apiParams?.countryCode && <ConfigurationDetailLine Icon={ProjectDetailsEqualizer} title="Country code">{params.apiParams?.countryCode as string}</ConfigurationDetailLine>}
    {params.apiParams?.page && <ConfigurationDetailLine Icon={ProjectDetailsEqualizer} title="Page">{params.apiParams?.page as string}</ConfigurationDetailLine>}
    {params.apiParams?.items_per_page && <ConfigurationDetailLine Icon={ProjectDetailsEqualizer} title="Items per page">{params.apiParams?.items_per_page as string}</ConfigurationDetailLine>}
    {params.apiParams?.seller_id && <ConfigurationDetailLine Icon={ProjectDetailsEqualizer} title="Seller ID">{params.apiParams?.seller_id as string}</ConfigurationDetailLine>}
    {params.apiParams?.condition && <ConfigurationDetailLine Icon={ProjectDetailsEqualizer} title="Condition">{params.apiParams?.condition as string}</ConfigurationDetailLine>}
    {params.apiParams?.buying_format && <ConfigurationDetailLine Icon={ProjectDetailsEqualizer} title="Buying format">{params.apiParams?.buying_format as string}</ConfigurationDetailLine>}
    {params.apiParams?.show_only && <ConfigurationDetailLine Icon={ProjectDetailsEqualizer} title="Show only">{params.apiParams?.show_only as string}</ConfigurationDetailLine>}
    {params.apiParams?.sort_by && <ConfigurationDetailLine Icon={ProjectDetailsEqualizer} title="Sort by">{params.apiParams?.sort_by as string}</ConfigurationDetailLine>}
  </>);
}

const RedfinProjectAsyncApiParams = ({project}: {project: ProjectConfigWithStats}) => {
  const params = (project.config || {});
  return (<>
    {params.apiParams?.raw && <ConfigurationDetailLine Icon={ProjectDetailsEqualizer} title="Raw data">Enabled</ConfigurationDetailLine>}
    {params.apiParams?.tld && <ConfigurationDetailLine Icon={ProjectDetailsEqualizer} title="Domain">{params.apiParams?.tld as string}</ConfigurationDetailLine>}
    {params.apiParams?.countryCode && <ConfigurationDetailLine Icon={ProjectDetailsEqualizer} title="Country code">{params.apiParams?.countryCode as string}</ConfigurationDetailLine>}
  </>);
}

const HomeDepotProjectAsyncApiParams = ({project}: {project: ProjectConfigWithStats}) => {
  const params = (project.config || {});
  return (<>
    {params.apiParams?.sortBy && <ConfigurationDetailLine Icon={ProjectDetailsEqualizer} title="Sort by">{params.apiParams?.sortBy as string}</ConfigurationDetailLine>}
    {params.apiParams?.minPrice && <ConfigurationDetailLine Icon={ProjectDetailsEqualizer} title="Min price">{params.apiParams?.minPrice as string}</ConfigurationDetailLine>}
    {params.apiParams?.maxPrice && <ConfigurationDetailLine Icon={ProjectDetailsEqualizer} title="Max price">{params.apiParams?.maxPrice as string}</ConfigurationDetailLine>}
    {params.apiParams?.page && <ConfigurationDetailLine Icon={ProjectDetailsEqualizer} title="Page">{params.apiParams?.page as string}</ConfigurationDetailLine>}
  </>);
}

const HomeDepotProductProjectAsyncApiParams = ({project}: {project: ProjectConfigWithStats}) => {
  const params = (project.config || {});
  return (<>
    {params.apiParams?.url && <ConfigurationDetailLine Icon={ProjectDetailsEqualizer} title="URL">{params.apiParams?.url as string}</ConfigurationDetailLine>}
    {params.apiParams?.gtin && <ConfigurationDetailLine Icon={ProjectDetailsEqualizer} title="GTIN">{params.apiParams?.gtin as string}</ConfigurationDetailLine>}
  </>);
}

const HomeDepotReviewsProjectAsyncApiParams = ({project}: {project: ProjectConfigWithStats}) => {
  const params = (project.config || {});
  return (<>
    {params.apiParams?.url && <ConfigurationDetailLine Icon={ProjectDetailsEqualizer} title="URL">{params.apiParams?.url as string}</ConfigurationDetailLine>}
    {params.apiParams?.gtin && <ConfigurationDetailLine Icon={ProjectDetailsEqualizer} title="GTIN">{params.apiParams?.gtin as string}</ConfigurationDetailLine>}
    {params.apiParams?.page && <ConfigurationDetailLine Icon={ProjectDetailsEqualizer} title="Page">{params.apiParams?.page as string}</ConfigurationDetailLine>}
  </>);
}


const sectionHeaderStyle = "font-semibold text-base text-gray-600 dark:text-neutral-600 mb-2 mt-2";
const ConfigurationDetails = ({project}: {project: ProjectConfigWithStats}) => {
  const configView = projectConfigToShow(project);
  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 p-4 gap-x-8">
    <div>
      <div className={sectionHeaderStyle}>Input</div>
      <div className="flex flex-col gap-2">
        {configView.input.map( (entry, idx) => (
          <ConfigurationDetailLine key={idx} Icon={icons[entry.icon]} title={entry.title}>
            {entry.component}
          </ConfigurationDetailLine>
        ))}
      </div>
    </div>
    <div>
      <div className={sectionHeaderStyle}>Output</div>
      <div className="flex flex-col gap-2">
        {configView.output.map( (entry, idx) => (
          <ConfigurationDetailLine key={idx} Icon={icons[entry.icon]} title={entry.title}>
            {entry.component}
          </ConfigurationDetailLine>
        ))}
        {configView.interval && (
          <ConfigurationDetailLine Icon={icons[configView.interval.icon]} title={configView.interval.title}>
            {configView.interval.component}
          </ConfigurationDetailLine>
        )}
      </div>
    </div>
    <div>
      <div className={sectionHeaderStyle}>Additional options &amp; filters</div>
      <div className="flex flex-col gap-2">
          {isAsyncUrlsProject(project.config.type) && <UrlProjectAsyncApiParams project={project}/>}
          {isAsyncUrlsLlmProject(project.config.type) && <UrlProjectAsyncApiParams project={project}/>}
          {isAmazonProject(project.config.type) && <AmazonProjectAsyncApiParams project={project}/>}
          {isGoogleProject(project.config.type) && <GoogleProjectAsyncApiParams project={project}/>}
          {isWalmartProject(project.config.type) && <WalmartProjectAsyncApiParams project={project}/>}
          {isEbayProject(project.config.type) && <EbayProjectAsyncApiParams project={project}/>}
          {isRedfinProject(project.config.type) && <RedfinProjectAsyncApiParams project={project}/>}
          {isHomeDepotSearchProject(project.config.type) && <HomeDepotProjectAsyncApiParams project={project}/>}
          {isHomeDepotProductProject(project.config.type) && <HomeDepotProductProjectAsyncApiParams project={project}/>}
          {isHomeDepotReviewsProject(project.config.type) && <HomeDepotReviewsProjectAsyncApiParams project={project}/>}
      </div>
    </div>
    </div>
  );
};

export default function ProjectDetails() {
  const user = useUser();
  const { currentProject, currentProjectLoading, fetchCurrentProject, clearCurrentProject } = useHostedScrapingProjects();
  const { projectId: projectIdParam} = useParams() as { projectId: string};
  const [ projectShouldRunSoon, setProjectShouldRunSoon ] = useState(true);

  // Demo project async lookup
  const [ demoProjectId, setDemoProjectId ] = useState<number | null>(null);
  const [ demoLookupDone, setDemoLookupDone ] = useState(false);
  const isDemo = projectIdParam === 'demo';

  // Read-only cost estimate. Pass currentProject directly: gating on loading would
  // blank the cost on every 10s refresh, since currentProject stays populated while
  // currentProjectLoading is true.
  const { cost } = useProjectCostEstimate(currentProject, 'hosted', { debounceMs: 0 });

  useEffect(() => {
    if (!isDemo) return;
    let cancelled = false;
    (async () => {
      try {
        const demo = await scraperApi.hostedScraping.demoProject();
        if (!cancelled) {
          setDemoProjectId(demo?.id ?? null);
        }
      } catch {
        if (!cancelled) {
          setDemoProjectId(null);
        }
      } finally {
        if (!cancelled) {
          setDemoLookupDone(true);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [ isDemo ]);

  const projectId = parseInt(projectIdParam);

  // Fetch current project on mount + periodic refresh (skip for demo route)
  useEffect(() => {
    if (isDemo || isNaN(projectId)) return;
    fetchCurrentProject(projectId);
    const timer = setInterval(() => fetchCurrentProject(projectId), 10_000);
    return () => {
      clearInterval(timer);
      clearCurrentProject();
    };
  }, [ isDemo, projectId, fetchCurrentProject, clearCurrentProject ]);

  const scheduleNow = useCallback(async() => {
    if (!projectShouldRunSoon) {
      if (currentProject?.config?.type) {
        trackDataPipelineRunJobNow(getSdeTrackingInfo(currentProject.config.type), projectId);
      }
      setProjectShouldRunSoon(true);
      await scraperApi.hostedScraping.scheduleNowProject(projectId);
      Toaster.success("New job successfully scheduled", "A new job will start in a few seconds. The started job will be visible in the list below.");
      await fetchCurrentProject(projectId);
    }
  }, [projectId, fetchCurrentProject, projectShouldRunSoon, currentProject]);

  const recentlyViewedPageContext = useRecentlyViewedPage();

  useEffect(() => {
    if (currentProject?.config?.type) {
      recentlyViewedPageContext?.setIconName(("DP_" + iconType("hosted-scraper", currentProject.config.type)) as IconName);
    }
  }, [ currentProject, recentlyViewedPageContext ])

  const num = (val: string | number | undefined) => {
    return toNumber(val) || 0;
  };

  useEffect(() => {
      if (currentProject && !currentProjectLoading) {
        if (
          (currentProject?.supposedToRunAt && compareDates(currentProject.supposedToRunAt, Date.now()) <= 0) ||  // project is scheduled to run immediately but hasn't been picked up by a worker yet
          (currentProject && num(currentProject.allJobsCount) > (num(currentProject.doneJobsCount) + num(currentProject.errorJobsCount) + num(currentProject.cancelledJobsCount)))  // project has unfinished jobs
        ) {
          setProjectShouldRunSoon(true);
        } else {
          setProjectShouldRunSoon(false);
        }
      }
    },
    [ currentProjectLoading, currentProject, setProjectShouldRunSoon ]
  );

  if (isDemo) {
    if (!demoLookupDone) {
      return <FullWidthLoadingSpinner/>;
    }
    if (demoProjectId === null) {
      return (<ProjectNotFound/>);
    }
    return <Navigate to={`/projects/${demoProjectId}`} replace={true} />;
  }

  if (currentProjectLoading && !currentProject) {
    return <FullWidthLoadingSpinner/>;
  }

  if (!currentProject) {
    return (<ProjectNotFound/>);
  }

  const project = currentProject;

  const remainingCredits = user ? user.creditsRemaining : 0;
  const shouldShowJobSkipWarning = project.enabled && cost !== undefined && cost > 0 && cost > remainingCredits;

  const runNowButton = (
    <Button
      icon={ { Icon: PlayIcon } }
      text="Run job now"
      className="button button-primary"
      disabled={ projectShouldRunSoon }
      size="MD"
      onClick={ scheduleNow }
    />
  );

  const titleButtons = (
    <div className="flex gap-2 mr-2">
      { projectShouldRunSoon && (
        <PureTooltip content={tooltips.runNowDisabled}>
          { runNowButton }
        </PureTooltip>
      )}
      { !projectShouldRunSoon && runNowButton }
      <Button
        text="Edit"
        className="button button-secondary"
        href={ `/projects/${ projectId }/edit` }
        size="MD"
      />
    </div>);

  // TODO: The background should be bg-gray-50 dark:bg-neutral-50 (249, 250, 251) but it's way darker than it should be
  return (
    <BorderLayout layout="vertical">
      <ProjectBreadcrumbPage readonly titleButtons={titleButtons} className="bg-slate-50" projectName={project.name} projectId={project.id}>
        <div className="flex flex-col gap-5 md:px-14 pt-10 pb-24 overflow-auto">
          {shouldShowJobSkipWarning && (
            <div className="w-full flex items-start gap-3 p-4 bg-warning-50 border border-warning-200 rounded">
              <ExclamationCircleIcon className="w-6 h-6 text-warning-600 shrink-0 mt-0.5" />
              <div className="flex flex-1 gap-2 items-start justify-between min-w-0">
                <div className="flex flex-col gap-2">
                  <p className="font-medium text-base text-neutral-900">Your scheduled job will be skipped due to low credits</p>
                  <ul className="list-disc text-sm text-neutral-700 ml-5">
                    <li>Credits needed: {cost ?? '?'}  |  Credits available: {remainingCredits}</li>
                    <li>Job scheduled to run on {formatDate(project.supposedToRunAt)}</li>
                  </ul>
                </div>
                <Button
                  text="Upgrade to run on schedule"
                  className="button button-primary shrink-0 whitespace-nowrap"
                  href={BillingPath}
                  size="SM"
                />
              </div>
            </div>
          )}
          <ProjectDetailsHead variant="hosted-scraper" configType={project.config.type}/>
          <div className="w-full border-t border-slate-200"/>

          <Block>
            <Tab.Group>
              <Tab.List className="text-gray dark:text-neutral-600 md-2 flex gap-x-6 border-b-[1px] border-slate-200">
                <TabButton>Overview</TabButton>
                <TabButton>Configuration</TabButton>
              </Tab.List>
              <Tab.Panels className="m-4">
                <Tab.Panel><BasicInfoAndStats project={project}/></Tab.Panel>
                <Tab.Panel><ConfigurationDetails project={project}/></Tab.Panel>
              </Tab.Panels>
            </Tab.Group>
          </Block>

          <JobList supposedToRunAt={project.supposedToRunAt} projectId={project.id}  projectShouldRunSoon={projectShouldRunSoon} scheduleNow={scheduleNow} />
        </div>
      </ProjectBreadcrumbPage>
    </BorderLayout>
  );
}
